import { logger } from '@/utils/logger';
import useCurrentReport from '@/components/reports/useCurrentReport';
import { FormattedHumanReadableRule } from '@/components/rules/components/FormattedHumanReadableRule';
import MatchingTransactionsCountCard from '@/components/rules/components/MatchingTransactionsCountCard';
import { RuleActionBuilder } from '@/components/rules/form/RuleActionBuilder';
import { RuleTriggerBuilder } from '@/components/rules/form/RuleTriggerBuilder';
import {
  RulePresets,
  RuleFormValues,
  createRuleFormSchema,
} from '@/components/rules/form/rule-form-types';
import { useIsMobile } from '@/components/ui/use-mobile';
import { useBackBlocker } from '@/hooks/use-back-blocker';
import useDebounced from '@/hooks/use-debounced';
import { useHumanReadableGenerator } from '@/hooks/use-human-readable-generator';
import { useIntersectionObserver } from '@/hooks/use-intersection-observer';
import { useInfiniteTransactions } from '@/hooks/use-infinite-transactions';
import useRules from '@/hooks/use-rules';
import appServices from '@/services/appServices';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionType,
  AppRule,
  AppRuleAction,
  AppRuleTrigger,
  TriggerType,
} from '@polybank/shared';
import { rawRulesUtils } from '@polybank/shared/rule-utils';
import { AIRuleResponse } from '@polybank/shared/types/ai-contracts';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Settings, Sparkles, Target, X, Zap } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

export interface RuleFormContentProps {
  presets?: RulePresets;
  rawRule?: string;
  targetGroupId?: string;
  onClose: () => void;
  isPanel?: boolean;
  scrollContainerRef?: (el: HTMLDivElement | null) => void;
}

export const RuleFormContent: React.FC<RuleFormContentProps> = ({
  presets,
  rawRule,
  targetGroupId,
  onClose,
  isPanel = false,
  scrollContainerRef,
}) => {
  const { t } = useTranslation();
  const ruleFormSchema = useMemo(() => createRuleFormSchema(t), [t]);
  const isMobile = useIsMobile();
  const { refetch: refetchTransactions } = useInfiniteTransactions();
  const { invalidate: invalidateReport } = useCurrentReport();
  const { generate: generateHumanReadable } = useHumanReadableGenerator();

  // Parse rawRule string to AIRuleResponse for metadata preservation
  const parsedRawRule = useMemo((): AIRuleResponse | undefined => {
    if (!rawRule) return undefined;
    try {
      return JSON.parse(rawRule) as AIRuleResponse;
    } catch (error) {
      logger.error('Failed to parse rawRule:', error);
      return undefined;
    }
  }, [rawRule]);

  // Convert rawRule to presets if provided
  const rawRulePresets = useMemo((): RulePresets | undefined => {
    if (!parsedRawRule) return undefined;
    return rawRulesUtils.toStructured(parsedRawRule);
  }, [parsedRawRule]);

  // Merge rawRule presets with provided presets (rawRule takes precedence)
  const mergedPresets = useMemo(() => {
    if (rawRulePresets) {
      return {
        ...presets,
        ...rawRulePresets,
      };
    }
    return presets;
  }, [rawRulePresets, presets]);

  // Memoize defaultValues to prevent form reset on re-renders
  const defaultValues = useMemo(
    () => ({
      title: mergedPresets?.title || '',
      description:
        parsedRawRule?.humanReadable || mergedPresets?.description || '',
      triggers: (mergedPresets?.triggers as AppRuleTrigger[]) || [
        { triggerType: TriggerType.DESCRIPTION_CONTAINS, triggerValue: '' },
      ],
      actions: (mergedPresets?.actions as AppRuleAction[]) || [
        { actionType: ActionType.SET_CATEGORY, actionValue: '' },
      ],
    }),
    [
      mergedPresets?.title,
      mergedPresets?.description,
      mergedPresets?.triggers,
      mergedPresets?.actions,
      parsedRawRule?.humanReadable,
    ]
  );

  const methods = useForm<RuleFormValues>({
    resolver: zodResolver(ruleFormSchema),
    mode: 'onChange',
    defaultValues,
    shouldUnregister: false,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = methods;

  // Watch triggers and actions to build a live AIRuleResponse for matching count
  const watchedTriggers = useWatch({ control, name: 'triggers' });
  const watchedActions = useWatch({ control, name: 'actions' });

  // Serialize watched values so useMemo detects deep changes within field arrays
  const triggersKey = JSON.stringify(watchedTriggers);
  const actionsKey = JSON.stringify(watchedActions);

  const liveRawRule = useMemo((): AIRuleResponse | undefined => {
    const triggers = watchedTriggers || [];
    const actions = watchedActions || [];
    const validTriggers = triggers.filter(
      (t) => t.triggerType && t.triggerValue?.trim()
    );
    const validActions = actions.filter(
      (a) => a.actionType && a.actionValue?.trim()
    );
    if (validTriggers.length === 0 || validActions.length === 0)
      return undefined;
    try {
      const built = rawRulesUtils.fromStructured(
        {
          triggers: validTriggers as AppRuleTrigger[],
          actions: validActions as AppRuleAction[],
        },
        { reasoning: parsedRawRule?.reasoning }
      );
      built.humanReadable = generateHumanReadable(built.conditions);
      return built;
    } catch {
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    triggersKey,
    actionsKey,
    parsedRawRule?.reasoning,
    generateHumanReadable,
  ]);

  // Build a rule from triggers only (with dummy action if needed) for matching count
  const liveTriggersOnlyRule = useMemo((): AIRuleResponse | undefined => {
    const triggers = watchedTriggers || [];
    const validTriggers = triggers.filter(
      (t) => t.triggerType && t.triggerValue?.trim()
    );
    if (validTriggers.length === 0) return undefined;
    const actions = watchedActions || [];
    const validActions = actions.filter(
      (a) => a.actionType && a.actionValue?.trim()
    );
    const actionsForBuild =
      validActions.length > 0
        ? (validActions as AppRuleAction[])
        : [
            {
              actionType: ActionType.SET_CATEGORY,
              actionValue: '_placeholder',
            } as AppRuleAction,
          ];
    try {
      return rawRulesUtils.fromStructured({
        triggers: validTriggers as AppRuleTrigger[],
        actions: actionsForBuild,
      });
    } catch {
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggersKey, actionsKey]);

  const [debouncedRule, isDebouncingRule] = useDebounced(
    liveTriggersOnlyRule,
    500
  );

  const [applyNow, setApplyNow] = useState(true);
  const { addItem, updateItem } = useRules();

  // For panel mode, manage our own scroll container
  const [panelScrollContainer, setPanelScrollContainer] =
    useState<HTMLDivElement | null>(null);

  const scrollContainer = isPanel ? panelScrollContainer : null;

  // Update form when presets change, rawRule changes, or when component mounts
  useEffect(() => {
    if (mergedPresets) {
      reset({
        title: mergedPresets.title || '',
        description:
          parsedRawRule?.humanReadable || mergedPresets.description || '',
        triggers: (mergedPresets.triggers as AppRuleTrigger[]) || [],
        actions: (mergedPresets.actions as AppRuleAction[]) || [],
      });
    } else {
      // Reset to empty form when opening without presets
      reset({
        title: '',
        description: '',
        triggers: [
          { triggerType: TriggerType.DESCRIPTION_CONTAINS, triggerValue: '' },
        ],
        actions: [{ actionType: ActionType.SET_CATEGORY, actionValue: '' }],
      });
    }
  }, [mergedPresets, rawRule, reset, parsedRawRule?.humanReadable]);

  useBackBlocker({
    enabled: !isPanel,
    onBack: useCallback(() => {
      onClose();
    }, [onClose]),
  });

  const { ref: inlineCountRef, isIntersecting: isInlineVisible } =
    useIntersectionObserver({
      root: isPanel ? scrollContainer : undefined,
      threshold: 0,
      initialIsIntersecting: true,
    });
  const showStickyCount = !!liveTriggersOnlyRule && !isInlineVisible;

  const executeMutation = useMutation({
    mutationFn: async (ruleId: string) => {
      const result = await appServices.rulesApi.executeRule(ruleId, true);
      return result;
    },
    onSuccess: (result) => {
      if (result?.success) {
        const data = result.data;
        toast.success(
          t('rules.ruleAppliedSuccess', {
            count: data?.transactionsUpdated || 0,
            time: data?.processingTimeMs || 0,
          })
        );
      } else {
        toast.error(t('rules.executeError'));
      }
    },
    onError: (error) => {
      logger.error('Failed to execute rule:', error);
      toast.error(t('rules.executeError'));
    },
  });

  const saveMutation = useMutation({
    mutationFn: (data: AppRule) => {
      if (presets?.id) {
        return appServices.rules.updateRule(presets.id, data);
      } else {
        return appServices.rules.createRule(data);
      }
    },
    onSuccess: async (data) => {
      if (presets?.id) {
        updateItem(data);
      } else {
        addItem(data);
      }

      // Execute the rule immediately if "apply now" is checked
      if (applyNow && data.$id) {
        await executeMutation.mutateAsync(data.$id);
      }

      refetchTransactions();
      invalidateReport();
      onClose();
      setApplyNow(false);
    },
    onError: (error) => {
      logger.error('Failed to create rule:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      toast.error(`${t('rules.createError')}: ${errorMessage}`);
    },
  });

  const onSubmit = useCallback(
    async (data: RuleFormValues) => {
      // Filter out invalid triggers and actions with proper trimming
      const validTriggers = data.triggers
        .filter(
          (trigger) => trigger.triggerType && trigger.triggerValue.trim() !== ''
        )
        .map((trigger) => ({
          ...trigger,
          triggerValue: trigger.triggerValue.trim(),
        }));

      const validActions = data.actions
        .filter(
          (action) => action.actionType && action.actionValue.trim() !== ''
        )
        .map(
          (action) =>
            ({
              ...action,
              actionValue: action.actionValue.trim(),
            }) satisfies AppRuleAction
        );

      if (validTriggers.length === 0 || validActions.length === 0) {
        return;
      }

      // If rawRule was provided, rebuild it using fromStructured() and trigger callback
      if (parsedRawRule) {
        const rebuiltRawRule = rawRulesUtils.fromStructured(
          {
            triggers: validTriggers,
            actions: validActions,
          },
          {
            reasoning: parsedRawRule.reasoning,
          }
        );

        // Generate humanReadable client-side
        rebuiltRawRule.humanReadable = generateHumanReadable(
          rebuiltRawRule.conditions
        );
        saveMutation.mutate({
          title: data.title.trim(),
          description: rebuiltRawRule.humanReadable,
          humanReadable: rebuiltRawRule.humanReadable,
          active: true,
          raw: JSON.stringify(rebuiltRawRule),
          ...(!presets?.id && targetGroupId
            ? { ruleGroups: targetGroupId as unknown as AppRule['ruleGroups'] }
            : {}),
        });
      } else {
        const translatedRule = rawRulesUtils.fromStructured({
          triggers: validTriggers,
          actions: validActions,
        });
        saveMutation.mutate({
          title: data.title.trim(),
          description: data.description.trim(),
          triggers: validTriggers as AppRuleTrigger[],
          actions: validActions as AppRuleAction[],
          active: true,
          raw: JSON.stringify(translatedRule),
          humanReadable: liveRawRule?.humanReadable || '',
          ...(!presets?.id && targetGroupId
            ? { ruleGroups: targetGroupId as unknown as AppRule['ruleGroups'] }
            : {}),
        });
      }
    },
    [
      parsedRawRule,
      generateHumanReadable,
      saveMutation,
      presets?.id,
      targetGroupId,
      liveRawRule?.humanReadable,
    ]
  );

  const handleScrollContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (isPanel) {
        setPanelScrollContainer(el);
      }
      scrollContainerRef?.(el);
    },
    [isPanel, scrollContainerRef]
  );

  const formContent = (
    <>
      <div className='relative'>
        {/* Background decoration */}
        <div className='absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/5 to-purple-600/5 rounded-full -translate-y-12 translate-x-12 pointer-events-none'></div>
        <div className='absolute top-4 right-4 w-16 h-16 bg-gradient-to-br from-purple-500/5 to-pink-600/5 rounded-full pointer-events-none'></div>

        <div className='relative p-6 sm:p-8 pb-6'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3 sm:gap-4'>
              <div className='flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-lg'>
                <Zap className='w-5 h-5 sm:w-6 sm:h-6' />
              </div>
              <div>
                <h2 className='text-xl sm:text-2xl font-semibold bg-clip-text text-slate-700'>
                  {presets?.id
                    ? t('createRuleDialog.editTitle')
                    : t('createRuleDialog.createTitle')}
                </h2>
                <p className='mt-1 sm:mt-2 text-sm sm:text-base text-slate-600 leading-relaxed'>
                  {presets?.id
                    ? t('createRuleDialog.editDescription')
                    : t('createRuleDialog.createDescription')}
                </p>
              </div>
            </div>
            {isPanel && (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                onClick={onClose}
                className='flex-shrink-0'
              >
                <X className='w-5 h-5' />
              </Button>
            )}
          </div>
        </div>
      </div>

      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className='px-6 sm:px-8 pb-6 sm:pb-8'
        >
          {/* Basic Information */}
          <div className='space-y-4 sm:space-y-6'>
            <div className='flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6'>
              <div className='flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-slate-500 to-slate-700 text-white shadow-sm'>
                <Settings className='w-4 h-4 sm:w-5 sm:h-5' />
              </div>
              <div>
                <h3 className='text-lg sm:text-xl font-semibold text-slate-900'>
                  {t('createRuleDialog.basicInfo')}
                </h3>
                <p className='text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1'>
                  {t('createRuleDialog.basicInfoDescription')}
                </p>
              </div>
            </div>

            <div className='grid gap-6 lg:gap-8'>
              <div className='space-y-2'>
                <Label
                  htmlFor='title'
                  className='text-sm font-medium text-slate-700'
                >
                  {t('createRuleDialog.ruleTitle')}
                </Label>
                <Controller
                  name='title'
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        id='title'
                        {...field}
                        placeholder={t('createRuleDialog.ruleTitlePlaceholder')}
                        className='h-11 text-base'
                      />
                      {errors.title && (
                        <p className='text-sm text-red-600'>
                          {errors.title.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>

              {parsedRawRule?.humanReadable && !liveRawRule && (
                <div className='space-y-2'>
                  <Controller
                    name='description'
                    control={control}
                    render={() => (
                      <FormattedHumanReadableRule
                        rawText={parsedRawRule.humanReadable || ''}
                      />
                    )}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Triggers */}
          <div className='space-y-4 sm:space-y-6 mt-4 sm:mt-6'>
            <div className='flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6'>
              <div className='flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-sm'>
                <Target className='w-4 h-4 sm:w-5 sm:h-5' />
              </div>
              <div>
                <h3 className='text-lg sm:text-xl font-semibold text-slate-900'>
                  {t('createRuleDialog.conditionsTitle')}
                </h3>
                <p className='text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1'>
                  {t('createRuleDialog.conditionsDescription')}
                </p>
              </div>
            </div>
            <RuleTriggerBuilder isMobile={isMobile} />
            {errors.triggers && (
              <p className='text-sm text-red-600'>{errors.triggers.message}</p>
            )}
          </div>

          {/* Actions */}
          <div className='space-y-4 sm:space-y-6 mt-4 sm:mt-6'>
            <div className='flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6'>
              <div className='flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 text-white shadow-sm'>
                <Sparkles className='w-4 h-4 sm:w-5 sm:h-5' />
              </div>
              <div>
                <h3 className='text-lg sm:text-xl font-semibold text-slate-900'>
                  {t('createRuleDialog.actionsTitle')}
                </h3>
                <p className='text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1'>
                  {t('createRuleDialog.actionsDescription')}
                </p>
              </div>
            </div>
            <RuleActionBuilder isMobile={isMobile} />
            {errors.actions && (
              <p className='text-sm text-red-600'>{errors.actions.message}</p>
            )}
          </div>

          {/* Live Rule Preview: Human Readable */}
          {liveRawRule && (
            <div className='mt-6 space-y-4'>
              <FormattedHumanReadableRule
                rawText={liveRawRule.humanReadable || ''}
              />
            </div>
          )}

          {/* Run Now Section */}
          <div className='mt-8 pt-6 border-t border-slate-200'>
            {/* Inline Matching Transactions Count */}
            {liveTriggersOnlyRule && (
              <div ref={inlineCountRef} className='mt-2'>
                <MatchingTransactionsCountCard
                  rule={debouncedRule}
                  isDebouncing={isDebouncingRule}
                />
              </div>
            )}
            <div className='mt-2 flex items-start gap-3 p-4 rounded-lg bg-gradient-to-br from-blue-50/50 to-purple-50/50 border border-blue-100/50'>
              <input
                type='checkbox'
                id='applyNow'
                checked={applyNow}
                onChange={(e) => setApplyNow(e.target.checked)}
                className='mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer'
              />
              <div className='flex-1'>
                <label
                  htmlFor='applyNow'
                  className='text-sm font-medium text-slate-900 cursor-pointer block mb-1'
                >
                  {presets?.id
                    ? t('createRuleDialog.applyUpdatedToExisting')
                    : t('createRuleDialog.applyToExisting')}
                </label>
                <p className='text-xs text-slate-600 leading-relaxed'>
                  {presets?.id
                    ? t('createRuleDialog.applyUpdatedDescription')
                    : t('createRuleDialog.applyToExistingDescription')}
                </p>
              </div>
              <Zap className='w-5 h-5 text-blue-600 flex-shrink-0' />
            </div>
          </div>
          {/* Form Actions */}
          <div className='px-6 flex flex-col sm:flex-row justify-end gap-4 pt-8 mt-8 border-t border-slate-200 bg-slate-50/30 -mx-8 rounded-b-lg'>
            <Button
              type='button'
              variant='outline'
              onClick={onClose}
              className='h-11 px-6 text-sm font-medium border-slate-300 hover:bg-slate-50'
            >
              {t('common.cancel')}
            </Button>
            <Button
              type='submit'
              disabled={saveMutation.isPending || !isValid}
              className='h-11 px-6 text-sm font-medium gap-2 bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200'
            >
              {saveMutation.isPending ? (
                <Loader2 className='w-4 h-4 animate-spin' />
              ) : (
                <Zap className='w-4 h-4' />
              )}
              {presets?.id
                ? t('createRuleDialog.updateRule')
                : t('rules.createRule')}
            </Button>
          </div>
        </form>
      </FormProvider>
      {/* Floating bottom: matching count card (shown when inline is scrolled out of view) */}
      {showStickyCount && liveTriggersOnlyRule && (
        <div className='sticky bottom-0 left-0 right-0 px-6 sm:px-8 pb-4 pt-2 border-t bg-background/95 backdrop-blur z-10'>
          <MatchingTransactionsCountCard
            rule={debouncedRule}
            isDebouncing={isDebouncingRule}
          />
        </div>
      )}
    </>
  );

  if (isPanel) {
    return (
      <div ref={handleScrollContainerRef} className='h-full overflow-y-auto'>
        {formContent}
      </div>
    );
  }

  return formContent;
};
